/**
 * Analytics + DOM events. Native counterpart to legacy `helpers.dispatchEvent`.
 *
 * ─── CHOOSING HOW TWO COMPONENTS TALK ───────────────────────────────────────────────────────────
 *
 * Alpine's own guidance is `$dispatch` for loose coupling and `Alpine.store()` for shared state.
 * Both are right; what neither the Alpine docs nor the Hyvä guide spell out is SCOPE, which is the
 * part that actually goes wrong. Use this table:
 *
 *   Child → an ancestor        emit()          bubbles to a common ancestor. Scoped: no global
 *   (a select inside a form)                   name to collide with, and the listener sits on the
 *                                              element that owns the meaning.
 *
 *   Independent components     dispatch() +    document-level. Use a NAMESPACED name
 *   (unrelated, not nested)    listen()        ("near-me:filtered"), because at this level every
 *                                              listener on the page can hear it.
 *
 *   Several read one value     Alpine.store()  reactive shared state. An event announces that
 *                                              something happened; a store IS the something.
 *
 * THE TRAP, learned the hard way: an `x-on:…` written on the component's OWN element evaluates in
 * THAT component's Alpine scope. A searchable-select dispatching a plain "change" and a consumer
 * listening for it on the same element resolves `evt` to the select's registry, not the consumer's
 * — so the handler silently is not a function. Listen on an ANCESTOR, and give the event a
 * namespaced name so a native `change` from any input in the same form cannot be mistaken for it.
 */
const Events = {};

/** Fire a DOM CustomEvent. Does NOT bubble — see emit() when a parent needs to hear it. */
Events.dispatchEvent = function (name, detail = {}, element = document) {
  element.dispatchEvent(new CustomEvent(name, { detail }));
};

/**
 * Announce something FROM a component TO whoever is listening above it.
 *
 * bubbles + composed, which is the whole point: dispatchEvent() above fires on one element and
 * stops there, so an ancestor listener never runs. `composed` lets it escape a shadow root too —
 * nothing here uses shadow DOM today, but an event that silently stops at a boundary is a bad
 * default to leave lying around.
 *
 * Namespace the name ("ss-change", "near-me:filtered"). A bare "change" is indistinguishable from
 * the native one every input in the form already fires.
 *
 * @param  {string}  name     namespaced event name
 * @param  {object}  detail   payload; include enough for one listener to serve several senders
 * @param  {Element} element  the component's own element — usually `this.$el` in Alpine
 */
Events.emit = function (name, detail = {}, element = document) {
  element.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
};

/**
 * Analytics dispatch: push to the GTM dataLayer AND fire a DOM CustomEvent of the same name.
 * Both are needed — a CustomEvent alone never reaches the dataLayer. Never throws.
 */
Events.dispatch = function (name, payload = {}) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);

  Events.dispatchEvent(name, payload);
};

/**
 * Subscribe to an event dispatched by the pair above. The handler receives the `detail` payload
 * directly — you rarely want the CustomEvent wrapper — with the raw event as a second argument.
 *
 * Returns an unsubscribe function, so a caller can tear down without re-deriving the arguments:
 *
 *   const stop = thryvHelpers.listen("connect_bar_click", (payload) => …);
 *   stop();
 *
 * @return {function} unsubscribe
 */
Events.listen = function (name, handler, element = document) {
  const wrapped = (event) => handler(event.detail, event);

  element.addEventListener(name, wrapped);

  return () => element.removeEventListener(name, wrapped);
};

/** listen(), but the handler runs at most once. Returns an unsubscribe function too. */
Events.listenOnce = function (name, handler, element = document) {
  const wrapped = (event) => handler(event.detail, event);

  element.addEventListener(name, wrapped, { once: true });

  return () => element.removeEventListener(name, wrapped);
};

/**
 * Collect several subscriptions and tear them ALL down with one call.
 *
 * listen() returns an unsubscribe each, which is fine for one and tedious for five — and a
 * component that forgets even one leaks a listener for the life of the page. Alpine's destroy()
 * hook is one line with this:
 *
 *   init()    { this.events = Events.group(); this.events.listen("near-me:filtered", …); }
 *   destroy() { this.events.stop(); }
 *
 * @return {{listen: function, listenOnce: function, stop: function, size: function}}
 */
Events.group = function () {
  const offs = [];

  return {
    listen(name, handler, element = document) {
      offs.push(Events.listen(name, handler, element));

      return this;
    },

    listenOnce(name, handler, element = document) {
      offs.push(Events.listenOnce(name, handler, element));

      return this;
    },

    /** Unsubscribe everything and empty the group, so calling it twice is harmless. */
    stop() {
      offs.splice(0).forEach((off) => off());
    },

    size() {
      return offs.length;
    },
  };
};

/**
 * Resolve when an event fires — the promise form of listenOnce().
 *
 * For coordinating with something that finishes later (a vendor script announcing itself, a modal
 * reporting that it closed) without nesting callbacks.
 *
 * ALWAYS PASS A TIMEOUT for anything that might never fire. Without one the promise stays pending
 * forever and whatever awaited it never runs again — a hang with no error, which is far harder to
 * diagnose than a rejection.
 *
 * @param  {number} timeout  ms; 0 waits indefinitely
 * @return {Promise<object>} the event detail
 */
Events.waitFor = function (name, { timeout = 0, element = document } = {}) {
  return new Promise((resolve, reject) => {
    let timer;

    const off = Events.listenOnce(
      name,
      (detail) => {
        clearTimeout(timer);
        resolve(detail);
      },
      element,
    );

    if (timeout > 0) {
      timer = setTimeout(() => {
        off();
        reject(new Error(`Timed out after ${timeout}ms waiting for "${name}"`));
      }, timeout);
    }
  });
};

export default Events;
